/**
 * Time for something that is not being performed live.
 *
 * During a run, musical time comes from `MusicClock`, which is anchored to the
 * audio context and cannot be paused, scrubbed or run at double speed — that is
 * exactly right for a game being played and exactly wrong for a recording being
 * examined. Rather than making `MusicClock` grow a pause button it does not
 * want, replay gets its own clock and the two never meet.
 *
 * The important property is that it is **advanced, not sampled**. Callers push
 * elapsed wall time in and the clock decides what that means, so a frame that
 * took 200 ms moves replay time by 200 ms and the player emits every event in
 * that span. A clock that read `performance.now()` itself would be identical
 * until the first dropped frame, at which point it would silently skip whatever
 * fell in the gap — which is the frame-count dependence §18 forbids.
 */
export class PlaybackClock {
  private time = 0;
  private rate = 1;
  private stopped = true;
  private duration: number;

  constructor(durationSeconds = Number.POSITIVE_INFINITY) {
    this.duration = durationSeconds;
  }

  get currentTime(): number {
    return this.time;
  }

  get playbackRate(): number {
    return this.rate;
  }

  /**
   * Faster or slower than life.
   *
   * Clamped above zero: a rate of zero is what `pause` is for, and a negative
   * one would ask the player to un-emit events it has already sent.
   */
  set playbackRate(value: number) {
    this.rate = Number.isFinite(value) && value > 0 ? value : 1;
  }

  get paused(): boolean {
    return this.stopped;
  }

  get durationSeconds(): number {
    return this.duration;
  }

  setDuration(seconds: number): void {
    this.duration = Number.isFinite(seconds) && seconds > 0 ? seconds : Number.POSITIVE_INFINITY;
  }

  get finished(): boolean {
    return this.time >= this.duration;
  }

  play(): void {
    this.stopped = false;
  }

  pause(): void {
    this.stopped = true;
  }

  /**
   * Moves time forward by real elapsed seconds.
   *
   * Returns the replay time actually advanced, which is not the same number
   * once `playbackRate` is involved. Paused clocks advance by nothing at all
   * rather than by a scaled zero, so a long pause cannot accumulate drift.
   */
  advance(deltaSeconds: number): number {
    if (this.stopped) return 0;
    if (!Number.isFinite(deltaSeconds) || deltaSeconds <= 0) return 0;
    const before = this.time;
    this.time = Math.min(this.duration, this.time + deltaSeconds * this.rate);
    return this.time - before;
  }

  /** Jumps. The player is responsible for reconciling what it has emitted. */
  seek(seconds: number): void {
    if (!Number.isFinite(seconds)) return;
    this.time = Math.max(0, Math.min(this.duration, seconds));
  }

  reset(): void {
    this.time = 0;
    this.stopped = true;
  }
}
